<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import StatCard from '@/components/admin/StatCard.vue'
import {
  useEspaceClientStatsStore,
  type StatsPeriod,
} from '@/stores/useEspaceClientStatsStore'

const store = useEspaceClientStatsStore()

const period = ref<StatsPeriod>('month')

const periodOptions: Array<{ value: StatsPeriod; label: string }> = [
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Année' },
]

const periodRangeLabel = computed(() => {
  if (!store.stats) return ''
  const since = new Date(store.stats.current.since).toLocaleDateString('fr-FR')
  const until = new Date(store.stats.current.until).toLocaleDateString('fr-FR')
  return `Du ${since} au ${until}`
})

async function refresh() {
  await store.fetchStats(period.value)
}

watch(period, refresh)
onMounted(refresh)
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Espace client — Statistiques" subtitle="Activité et usage de l'espace client par les contacts" />

    <!-- Period selector -->
    <div class="mt-6 flex items-center gap-4 flex-wrap">
      <div role="tablist" class="tabs tabs-boxed" aria-label="Période d'analyse">
        <button
          v-for="opt in periodOptions"
          :key="opt.value"
          role="tab"
          :aria-selected="period === opt.value"
          class="tab"
          :class="{ 'tab-active': period === opt.value }"
          @click="period = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div v-if="periodRangeLabel" class="text-sm text-base-content/60">
        {{ periodRangeLabel }}
      </div>
    </div>

    <!-- Error -->
    <div v-if="store.error" class="alert alert-error mt-6">
      <span>{{ store.error }}</span>
    </div>

    <!-- Groupe Sociétés -->
    <section class="mt-8" aria-labelledby="group-companies">
      <h2 id="group-companies" class="text-lg font-semibold mb-3 text-base-content/80">Sociétés</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label="Sociétés avec accès"
          :value="store.stats?.stats.companiesWithAccess.value ?? null"
          :loading="store.isLoading"
          hide-delta
        />
        <StatCard
          label="Sociétés actives"
          :value="store.stats?.stats.companiesActive.value ?? null"
          :delta="store.stats?.stats.companiesActive.delta ?? null"
          :delta-sign="store.stats?.stats.companiesActive.deltaSign ?? null"
          :loading="store.isLoading"
          hint="Au moins un contact connecté sur la période"
        />
      </div>
    </section>

    <!-- Groupe Contacts -->
    <section class="mt-8" aria-labelledby="group-contacts">
      <h2 id="group-contacts" class="text-lg font-semibold mb-3 text-base-content/80">Contacts</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label="Contacts avec accès"
          :value="store.stats?.stats.contactsWithAccess.value ?? null"
          :loading="store.isLoading"
          hide-delta
        />
        <StatCard
          label="Contacts actifs"
          :value="store.stats?.stats.contactsActive.value ?? null"
          :delta="store.stats?.stats.contactsActive.delta ?? null"
          :delta-sign="store.stats?.stats.contactsActive.deltaSign ?? null"
          :loading="store.isLoading"
          hint="Connectés sur la période"
        />
      </div>
    </section>

    <!-- Groupe Support -->
    <section class="mt-8" aria-labelledby="group-support">
      <h2 id="group-support" class="text-lg font-semibold mb-3 text-base-content/80">Support</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label="Tickets créés depuis l'espace client"
          :value="store.stats?.stats.ticketsCreated.value ?? null"
          :delta="store.stats?.stats.ticketsCreated.delta ?? null"
          :delta-sign="store.stats?.stats.ticketsCreated.deltaSign ?? null"
          :loading="store.isLoading"
        />
      </div>
    </section>
  </div>
</template>
